/**
 * SubunitPropertiesPanel — Edit properties for a selected subunit
 * Uses explicit Save button pattern (no auto-save on blur).
 * Sections: Identity, Dimensions, Position, Elevation, Space Deduction
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSubunitMutations, subunitKeys } from '@/hooks/useSubunits'
import {
  SUBUNIT_CATEGORY_LABELS,
  type Subunit,
  type SubunitCategory,
  type UpdateSubunitInput,
} from '@/types/subunit'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ArrowLeft, Copy, Trash2, RotateCcw, Save } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useSubunitStore } from '@/stores/subunitStore'

interface SubunitPropertiesPanelProps {
  subunit: Subunit
  onBack: () => void
  parentDimensions?: { width: number; depth: number }
}

const CATEGORIES: SubunitCategory[] = ['office', 'storage', 'shop', 'custom']

export function SubunitPropertiesPanel({ subunit, onBack, parentDimensions }: SubunitPropertiesPanelProps) {
  const { update, duplicate, remove } = useSubunitMutations()
  const queryClient = useQueryClient()

  // Snapshot of last-saved server state — dirty tracking compares against this
  const savedRef = useRef<Subunit>(subunit)

  // Push overrides to all subunit list queries in cache (live 3D preview, no DB round-trip)
  const pushToCache = useCallback((patch: Partial<Subunit>) => {
    const id = subunit.id
    const queries = queryClient.getQueriesData<Subunit[]>({ queryKey: subunitKeys.lists() })
    for (const [key] of queries) {
      queryClient.setQueryData<Subunit[]>(key, (old) =>
        old?.map((s) => (s.id === id ? { ...s, ...patch } : s))
      )
    }
  }, [queryClient, subunit.id])

  // Local form state
  const [name, setName] = useState(subunit.name)
  const [unitCode, setUnitCode] = useState(subunit.unitCode)
  const [category, setCategory] = useState(subunit.category)
  const [width, setWidth] = useState(subunit.width.toString())
  const [depth, setDepth] = useState(subunit.depth.toString())
  const [height, setHeight] = useState(subunit.height.toString())
  const [offsetX, setOffsetX] = useState(subunit.offsetX.toString())
  const [offsetY, setOffsetY] = useState(subunit.offsetY.toString())
  const [elevationFt, setElevationFt] = useState(subunit.elevationFt.toString())
  const [clearanceBufferFt, setClearanceBufferFt] = useState(subunit.clearanceBufferFt.toString())
  const [deductFromParent, setDeductFromParent] = useState(subunit.deductFromParent)

  const constrainToParent = useSubunitStore((s) => s.constrainToParent)

  /** Clamp offset to parent bounds if constraint is on.
   *  X: edge-origin (0 = left edge), Y: center-origin (0 = center of depth) */
  const clampOffset = useCallback((ox: number, oy: number, w: number, d: number): [number, number] => {
    if (!constrainToParent || !parentDimensions) return [ox, oy]
    const halfSubW = w / 2
    const halfSubD = d / 2
    // X: edge-origin
    const minX = halfSubW
    const maxX = parentDimensions.width - halfSubW
    // Y: center-origin
    const halfParentD = parentDimensions.depth / 2
    const minY = -halfParentD + halfSubD
    const maxY = halfParentD - halfSubD
    return [
      minX > maxX ? parentDimensions.width / 2 : Math.max(minX, Math.min(maxX, ox)),
      minY > maxY ? 0 : Math.max(minY, Math.min(maxY, oy)),
    ]
  }, [constrainToParent, parentDimensions])

  // Full reset when switching subunit
  useEffect(() => {
    savedRef.current = subunit
    resetForm()
  }, [subunit.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Restore saved state to cache on unmount (discard unsaved drag/visual changes)
  useEffect(() => {
    return () => {
      const saved = savedRef.current
      const queries = queryClient.getQueriesData<Subunit[]>({ queryKey: subunitKeys.lists() })
      for (const [key] of queries) {
        queryClient.setQueryData<Subunit[]>(key, (old) =>
          old?.map((s) => (s.id === saved.id ? saved : s))
        )
      }
    }
  }, [queryClient])  

  // Sync position from external changes (drag)
  useEffect(() => {
    setOffsetX(subunit.offsetX.toString())
    setOffsetY(subunit.offsetY.toString())
  }, [subunit.offsetX, subunit.offsetY])

  // Live preview — push visual fields to cache so 3D model reflects form edits in real-time
  useEffect(() => {
    pushToCache({
      width: parseFloat(width) || subunit.width,
      depth: parseFloat(depth) || subunit.depth,
      height: parseFloat(height) || subunit.height,
      offsetX: parseFloat(offsetX) || 0,
      offsetY: parseFloat(offsetY) || 0,
      elevationFt: parseFloat(elevationFt) || 0,
    })
  }, [width, depth, height, offsetX, offsetY, elevationFt, pushToCache, subunit.width, subunit.depth, subunit.height])

  // Auto-toggle deductFromParent when elevation changes
  const handleElevationChange = (val: string) => {
    setElevationFt(val)
    const numVal = parseFloat(val) || 0
    // If setting elevation > 0, auto-suggest deductFromParent = false
    if (numVal > 0 && deductFromParent) {
      setDeductFromParent(false)
    }
    // If setting back to 0, auto-suggest deductFromParent = true
    if (numVal === 0 && !deductFromParent) {
      setDeductFromParent(true)
    }
  }

  const resetForm = () => {
    const saved = savedRef.current
    // Restore cache to last-saved state (undoes drag visual changes)
    const queries = queryClient.getQueriesData<Subunit[]>({ queryKey: subunitKeys.lists() })
    for (const [key] of queries) {
      queryClient.setQueryData<Subunit[]>(key, (old) =>
        old?.map((s) => (s.id === saved.id ? saved : s))
      )
    }
    setName(saved.name)
    setUnitCode(saved.unitCode)
    setCategory(saved.category)
    setWidth(saved.width.toString())
    setDepth(saved.depth.toString())
    setHeight(saved.height.toString())
    setOffsetX(saved.offsetX.toString())
    setOffsetY(saved.offsetY.toString())
    setElevationFt(saved.elevationFt.toString())
    setClearanceBufferFt(saved.clearanceBufferFt.toString())
    setDeductFromParent(saved.deductFromParent)
  }

  // Computed values
  const computedSqft = (parseFloat(width) || 0) * (parseFloat(depth) || 0)
  const isElevated = (parseFloat(elevationFt) || 0) > 0

  // Dirty tracking — compare form against last-saved server state (not cache)
  const saved = savedRef.current
  const isDirty =
    name !== saved.name ||
    unitCode !== saved.unitCode ||
    category !== saved.category ||
    width !== saved.width.toString() ||
    depth !== saved.depth.toString() ||
    height !== saved.height.toString() ||
    offsetX !== saved.offsetX.toString() ||
    offsetY !== saved.offsetY.toString() ||
    elevationFt !== saved.elevationFt.toString() ||
    clearanceBufferFt !== saved.clearanceBufferFt.toString() ||
    deductFromParent !== saved.deductFromParent

  const handleSave = () => {
    const w = parseFloat(width) || subunit.width
    const d = parseFloat(depth) || subunit.depth
    let ox = parseFloat(offsetX) || 0
    let oy = parseFloat(offsetY) || 0
    ;[ox, oy] = clampOffset(ox, oy, w, d)
    const data: UpdateSubunitInput = {
      name,
      unitCode,
      category,
      width: w,
      depth: d,
      height: parseFloat(height) || subunit.height,
      offsetX: ox,
      offsetY: oy,
      elevationFt: parseFloat(elevationFt) || 0,
      clearanceBufferFt: parseFloat(clearanceBufferFt) || 1.5,
      deductFromParent,
    }
    update.mutate({ id: subunit.id, data }, {
      onSuccess: () => {
        // Update saved snapshot so dirty tracking resets
        savedRef.current = { ...savedRef.current, ...data, sqft: w * d }
      },
    })
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden" onWheel={(e) => e.stopPropagation()}>
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b shrink-0">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium truncate">{subunit.name}</span>
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <div className="p-4 space-y-5">
          {/* Identity */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Identity</h4>

            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-8 text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Unit Code</Label>
                <Input
                  value={unitCode}
                  onChange={(e) => setUnitCode(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Category</Label>
                <Select
                  value={category}
                  onValueChange={(v) => setCategory(v as SubunitCategory)}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c} className="text-xs">
                        {SUBUNIT_CATEGORY_LABELS[c]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          <Separator />

          {/* Dimensions */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Dimensions (ft)</h4>

            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Width</Label>
                <Input
                  type="number"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  className="h-8 text-sm"
                  min={1}
                  step={1}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Depth</Label>
                <Input
                  type="number"
                  value={depth}
                  onChange={(e) => setDepth(e.target.value)}
                  className="h-8 text-sm"
                  min={1}
                  step={1}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Height</Label>
                <Input
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  className="h-8 text-sm"
                  min={1}
                  step={1}
                />
              </div>
            </div>

            <div className="text-xs text-muted-foreground">
              Area: {computedSqft.toLocaleString()} sqft
            </div>
          </section>

          <Separator />

          {/* Position */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Position in Parent (ft)</h4>
            <p className="text-[10px] text-muted-foreground">Relative to parent unit origin</p>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Offset X</Label>
                <Input
                  type="number"
                  value={offsetX}
                  onChange={(e) => setOffsetX(e.target.value)}
                  onBlur={() => {
                    const w = parseFloat(width) || subunit.width
                    const d = parseFloat(depth) || subunit.depth
                    const [cx] = clampOffset(parseFloat(offsetX) || 0, parseFloat(offsetY) || 0, w, d)
                    setOffsetX(cx.toString())
                  }}
                  className="h-8 text-sm"
                  step={1}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Offset Y</Label>
                <Input
                  type="number"
                  value={offsetY}
                  onChange={(e) => setOffsetY(e.target.value)}
                  onBlur={() => {
                    const w = parseFloat(width) || subunit.width
                    const d = parseFloat(depth) || subunit.depth
                    const [, cy] = clampOffset(parseFloat(offsetX) || 0, parseFloat(offsetY) || 0, w, d)
                    setOffsetY(cy.toString())
                  }}
                  className="h-8 text-sm"
                  step={1}
                />
              </div>
            </div>
          </section>

          <Separator />

          {/* Elevation */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Elevation</h4>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Elevation (ft)</Label>
                <Input
                  type="number"
                  value={elevationFt}
                  onChange={(e) => handleElevationChange(e.target.value)}
                  className="h-8 text-sm"
                  min={0}
                  step={1}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Clearance Buffer (ft)</Label>
                <Input
                  type="number"
                  value={clearanceBufferFt}
                  onChange={(e) => setClearanceBufferFt(e.target.value)}
                  className="h-8 text-sm"
                  min={0}
                  step={0.5}
                />
              </div>
            </div>

            {isElevated && (
              <p className="text-[10px] text-blue-600">
                Mezzanine level at {elevationFt}ft
              </p>
            )}
          </section>

          <Separator />

          {/* Space Deduction */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Space Deduction</h4>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs">Deduct from parent sqft</Label>
                {isElevated && !deductFromParent && (
                  <p className="text-[10px] text-muted-foreground">(auto: mezzanine)</p>
                )}
              </div>
              <Switch
                checked={deductFromParent}
                onCheckedChange={(v) => setDeductFromParent(v)}
              />
            </div>

            {deductFromParent && (
              <p className="text-[10px] text-orange-600">
                Deducts {computedSqft.toLocaleString()} sqft from parent hangar
              </p>
            )}
          </section>

          <Separator />

          {/* Actions — icon buttons + Save */}
          <section className="flex items-center gap-1.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={!isDirty}
                    onClick={resetForm}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom"><p>Reset</p></TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => duplicate.mutate(subunit)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom"><p>Duplicate</p></TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => {
                      remove.mutate(subunit.id)
                      onBack()
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom"><p>Delete</p></TooltipContent>
              </Tooltip>

            <div className="flex-1" />

            <Button
              size="sm"
              disabled={!isDirty || update.isPending}
              onClick={handleSave}
            >
              <Save className="h-3.5 w-3.5 mr-1.5" />
              {update.isPending ? 'Saving...' : 'Save'}
            </Button>
          </section>
        </div>
      </ScrollArea>
    </div>
  )
}
